import React, { useState } from 'react';
import { PermissionMatrix } from '@clareefai/ui';
import { toast } from '@clareefai/ui';
import { useTranslation } from 'react-i18next';
import {
  useGetRolePermissionsQuery,
  useGetDefaultPermissionsQuery,
  useUpdateRolePermissionsMutation,
  useResetRolePermissionsMutation,
} from '../../store/vendorApi';
import { Loader } from 'lucide-react';

const PermissionsSettings: React.FC = () => {
  const { t } = useTranslation();
  const { data: rolePerms, isLoading: isLoadingPerms } = useGetRolePermissionsQuery();
  const { data: defaults, isLoading: isLoadingDefaults } = useGetDefaultPermissionsQuery();
  const [updateRolePermissions, { isLoading: isSaving }] = useUpdateRolePermissionsMutation();
  const [resetRolePermissions] = useResetRolePermissionsMutation();

  const [localMatrix, setLocalMatrix] = useState<Record<string, string[]> | null>(null);

  const currentMatrix = localMatrix ?? rolePerms?.rolePermissions ?? {};

  const handleChange = (matrix: Record<string, string[]>) => {
    setLocalMatrix(matrix);
  };

  const handleSave = async () => {
    try {
      await updateRolePermissions({ rolePermissions: currentMatrix as any }).unwrap();
      setLocalMatrix(null);
      toast.success(t('settings.permissions.saveSuccess'));
    } catch {
      toast.error(t('settings.permissions.saveError'));
    }
  };

  const handleReset = async () => {
    try {
      await resetRolePermissions().unwrap();
      setLocalMatrix(null);
      toast.success(t('settings.permissions.resetSuccess'));
    } catch {
      toast.error(t('settings.permissions.resetError'));
    }
  };

  if (isLoadingPerms || isLoadingDefaults) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!defaults) {
    return <p className="text-gray-500">{t('settings.permissions.loadError')}</p>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('settings.permissions.heading')}</h2>
      <p className="text-gray-600 mb-6">
        {t('settings.permissions.description')}
      </p>
      <PermissionMatrix
        permissions={defaults.allPermissions}
        roles={defaults.allRoles}
        matrix={currentMatrix}
        defaults={defaults.defaults}
        onChange={handleChange}
        onSave={handleSave}
        onReset={handleReset}
        isSaving={isSaving}
        isCustomized={rolePerms?.isCustomized ?? false}
      />
    </div>
  );
};

export default PermissionsSettings;
